import React, { useState, useEffect, useMemo } from 'react';
import styled from "@emotion/styled";
import { Dialog, DialogContent, DialogActions, Button, Typography, Box, Chip, IconButton, LinearProgress, Grid, InputLabel, TextField, Select, MenuItem, Switch, CircularProgress, FormControl, Tooltip, useMediaQuery } from "@mui/material";
import { Close, Edit, Add, AccessTime, CalendarToday, Group, CheckCircle, Warning, Delete, ViewKanban, DescriptionOutlined, AttachFileOutlined, InfoOutlined, LanguageOutlined } from "@mui/icons-material";
import { useNavigate } from 'react-router-dom';
import StatusChip from './StatusChip';
import PriorityChip from './PriorityChip';
import SkillChip from './SkillChip';
import DepartmentChip from './DepartmentChip';
import MarkdownDisplay from './MarkdownDisplay';
import MarkdownEditor from './MarkdownEditor';
import UserMenuChip from './UserMenuChip';
import TaskBlockersSection from './TaskBlockersSection';
import TaskChecklistSection from './TaskChecklistSection';
import { triggerRefresh } from "../redux/slices/refreshTriggers";
import { useSelector, useDispatch } from 'react-redux';
import { setUpdateTask, toggleTaskBoard, getTask } from '../redux/ducks/task';
import { getDepartments } from '../redux/ducks/department';
import { addUpTime, convertMinutesToHours } from '../utils/timeUtils';
import { useHasPermission } from '../utils/permissionUtils';
import { useIsLocked } from '../utils/lockUtils';
import { externalBoardLink } from '../utils/kanbanColumns';
import AttachmentBox from './AttachmentBox';
import FilePondUploader from './FilePondUploader';
import { transformAttachments, restoreAttachments } from '../utils/uploadUtils';
import AppDatePicker from './AppDatePicker';
import UserAssignmentDialog from './UserAssignmentDialog';
import AddTimeRecordDialog from './AddTimeRecordDialog';
import { postTime } from '../redux/ducks/time';
import { showAlert } from '../redux/slices/alerts';
import { differenceInDays, formatDate, formatDueLabel, toLocalDay } from '../utils/dateUtils';
import { selectableSkills, skillOptionLabel } from '../utils/skillUtils';
import { setTagAssignments } from '../redux/ducks/tag';
import TagPicker, { tagSourceNames, useTagDraft } from './TagPicker';
import TagContextDetails, { hasHumanContext } from './TagContextDetails';

const StyledDialog = styled(Dialog)(({ theme, fullScreen }) => ({
    '& .MuiDialog-paper': {
        borderRadius: fullScreen ? 0 : theme.shape.borderRadius * 2,
    },
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2.5),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1.5, 2),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(2, 2.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const TaskTitle = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.h5.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: 1.3,
    overflowWrap: 'anywhere',
}));

const HeaderActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0,
}));

const Section = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    '&:last-of-type': {
        marginBottom: 0,
    },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.25),
    color: theme.palette.text.secondary,
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightSemiBold,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    lineHeight: 1.5,
}));

const InfoPanel = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.surface.inset,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    height: '100%',
}));

const ChipRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    alignItems: 'center',
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.75),
}));

const EmptyText = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
}));

const AttachmentList = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
}));

const TeamBlock = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
}));

const MetaRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    minHeight: 32,
    '& + &': {
        marginTop: theme.spacing(1.25),
    },
}));

const MetaLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
}));

const MetaValue = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    fontWeight: theme.typography.fontWeightMedium,
}));

const BudgetBar = styled(LinearProgress)(({ theme }) => ({
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.surface.track,
}));

const BudgetText = styled(Typography, { shouldForwardProp: (prop) => prop !== 'over' })(({ theme, over }) => ({
    marginTop: theme.spacing(1),
    fontSize: theme.typography.size.md,
    fontWeight: theme.typography.fontWeightMedium,
    color: over ? theme.palette.error.main : theme.palette.text.primary,
}));

const CaptionText = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(0.5),
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
}));

const AddTimeButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(1.5),
}));

const TaskDetailsDialog = ({ task, onClose, onDeleteTask, editMode = false }) => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const skills = useSelector(state => state.skills);
    const [isEditing, setIsEditing] = useState(editMode);
    const [localTask, setLocalTask] = useState(task);
    const [selectedSkillId, setSelectedSkillId] = useState(task.skill?._id || '');
    const [selectedDepartmentId, setSelectedDepartmentId] = useState(task.department?._id || '');
    const [departments, setDepartments] = useState([]);
    const [localTaskAttachments, setLocalTaskAttachments] = useState(transformAttachments(task.attachments || []));
    const [editedAttachments, setEditedAttachments] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const canEditProjectTask = hasPermission('editProjectTask');
    const canDeleteProjectTask = hasPermission('deleteProjectTask');
    const canAssignTask = hasPermission('assignTask');
    const canAddTime = hasPermission('addTime');
    const canEditCompletedTask = hasPermission('editCompletedTasks');
    const [addTimeDialogOpen, setAddTimeDialogOpen] = useState(false);
    const currentUserId = useSelector(state => state.auth.userId);
    const isResponsibleForTask = !!task?.responsible?._id && !!currentUserId && String(task.responsible._id) === String(currentUserId);
    const project = useSelector(state => state.project.currentProject);
    const projectTasks = useMemo(() => project?.tasks || [], [project?.tasks]);
    const taskProject = useMemo(() => {
        const taskProjectId = task?.project?._id || task?.project;
        if (taskProjectId && project?._id && String(project._id) === String(taskProjectId)) return project;
        return task?.project?._id ? task.project : null;
    }, [task?.project, project]);
    const externalBoard = useMemo(() => externalBoardLink(taskProject?.externalBoard), [taskProject?.externalBoard]);
    const isLocked = useIsLocked(project?.account, project?.account?.client) || !!taskProject?.isDeleted;
    const isTaskCompleted = !!localTask.completedDate || localTask.status === 'Completed';
    const editsBlocked = isLocked || (isTaskCompleted && !canEditCompletedTask);
    const canSeeInternal = hasPermission('canSeeInternal');
    const navigate = useNavigate();
    const [boardToggleBusy, setBoardToggleBusy] = useState(false);
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const taskRefreshTrigger = useSelector(state => state.refreshTriggers.task);
    const fullScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const taskTags = Array.isArray(localTask?.tags) ? localTask.tags : [];
    const taskContext = localTask?.context ?? null;
    const tagsClosed = !!localTask?.completedDate;
    const tagsEditable = isEditing && !tagsClosed;
    const tagDraft = useTagDraft({ enabled: tagsEditable, targetType: 'Task', targetId: localTask?._id, tags: taskTags, prefillInherited: !!localTask?.tagsStale });
    const tagClientId = taskProject?.account?.client?._id || taskProject?.account?.client || localTask?.account?.client?._id || localTask?.account?.client || null;
    const tagClientName = taskProject?.account?.client?.name || localTask?.account?.client?.name;
    const tagSources = tagSourceNames(taskProject, taskProject?.group, taskProject?.account, taskProject?.account?.client);

    useEffect(() => {
        setLocalTask(task);
        setSelectedSkillId(task.skill?._id || '');
        setSelectedDepartmentId(task.department?._id || '');
    }, [task]);

    useEffect(() => {
        dispatch(getDepartments(0, 100, (result) => setDepartments(result || [])));
    }, [dispatch]);

    // When editing (via the edit button or opened directly in edit mode), inherit the
    // project's skill/department for a task that doesn't have one set yet.
    useEffect(() => {
        if (!isEditing) return;
        setSelectedSkillId((prev) => prev || project?.skill?._id || project?.skill || '');
        setSelectedDepartmentId((prev) => prev || project?.department?._id || project?.department || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditing, localTask?._id]);

    useEffect(() => {
        if (!localTask?._id) return;
        if (taskRefreshTrigger === 0) return;
        dispatch(getTask(localTask._id, (data) => {
            if (data) {
                setLocalTask(data);
            }
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskRefreshTrigger]);

    useEffect(() => {
        if (!canSeeInternal || !task?._id || task.context !== undefined) return;
        dispatch(getTask(task._id, (data) => {
            if (data) setLocalTask(data);
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [task?._id]);

    if (!localTask) {
        return null;
    }

    const usedHours = addUpTime(convertMinutesToHours(localTask.time.total));
    const budgetedHours = localTask.budget?.budgeted;
    const timeSpentPercentage = Math.min(100, (usedHours / budgetedHours) * 100);
    const overBudget = budgetedHours > 0 && usedHours > budgetedHours;
    const remainingDays = differenceInDays(formatDate(localTask.dueDate), toLocalDay());
    const deadlineStatus = remainingDays > 7 ? 'On Track' : remainingDays >= 0 ? 'Upcoming' : 'Delayed';

    const handleEdit = () => {
        setIsEditing(true);
        setEditedAttachments(transformAttachments(localTask.attachments || []));
    };

    const handleSave = () => {
        if (editsBlocked) return;
        const tagAssignments = tagsEditable && tagDraft.dirty ? tagDraft.assignments : null;
        const updatedTask = {
            ...localTask,
            skill: selectedSkillId,
            department: selectedDepartmentId || null,
            attachments: restoreAttachments(editedAttachments, localTaskAttachments),
        };
        dispatch(setUpdateTask(updatedTask._id, updatedTask, (response) => {
            if (response && response.task) {
                setLocalTask((prev) => ({ ...response.task, tags: prev.tags, context: prev.context }));
                setLocalTaskAttachments(transformAttachments(response.task.attachments || []));
                setSelectedSkillId(response.task.skill?._id || '');
                setSelectedDepartmentId(response.task.department?._id || '');
                const finish = () => {
                    dispatch(triggerRefresh({ triggerName: 'project' }));
                    setIsEditing(false);
                    if (editMode) {
                        onClose(response.task);
                    }
                };
                if (!tagAssignments) {
                    finish();
                    return;
                }
                dispatch(setTagAssignments('Task', response.task._id, tagAssignments, (data) => {
                    if (!data) {
                        dispatch(triggerRefresh({ triggerName: 'project' }));
                        return;
                    }
                    setLocalTask((prev) => ({ ...prev, tags: data.tags || [], ...(data.context !== undefined && { context: data.context }), ...(tagAssignments.inheritedTags && { tagsStale: false }) }));
                    finish();
                }));
            }
        }));
    };

    const handleChange = (field) => (event) => {
        if (field === 'skill') {
            setSelectedSkillId(event.target.value);
        } else if (field === 'department') {
            setSelectedDepartmentId(event.target.value);
        } else {
            setLocalTask({ ...localTask, [field]: event.target.value });
        }
    };

    const handleSwitchChange = (field) => (event) => {
        setLocalTask({ ...localTask, [field]: event.target.checked });
    };

    const handleOpenBoard = () => {
        const accountSlug = taskProject?.account?.slug || task?.account?.slug;
        const boardProjectId = taskProject?._id || task?.project?._id || task?.project;
        if (!accountSlug || !boardProjectId || !localTask?._id) return;
        navigate(`/account/${accountSlug}/project/${boardProjectId}/task/${localTask._id}/board`);
    };

    const handleToggleBoard = (event) => {
        if (isLocked || boardToggleBusy) return;
        const nextValue = event.target.checked;
        setBoardToggleBusy(true);
        dispatch(toggleTaskBoard(localTask._id, nextValue, (data) => {
            setBoardToggleBusy(false);
            if (data?.task) {
                setLocalTask((prev) => ({ ...prev, hasBoard: data.task.hasBoard }));
                dispatch(triggerRefresh({ triggerName: 'project' }));
            }
        }));
    };

    const handleTeamAssigned = () => {
        dispatch(getTask(localTask._id, (data) => {
            if (data) setLocalTask(data);
        }));
        dispatch(triggerRefresh({ triggerName: 'project' }));
    };

    const handleBlockersUpdated = (updatedTask) => {
        if (updatedTask) {
            setLocalTask((prev) => ({ ...prev, blockedBy: updatedTask.blockedBy, readiness: updatedTask.readiness }));
        }
    };

    const handleAddTime = (timeData) => {
        dispatch(postTime('task', localTask._id, timeData, (response) => {
            if (response && response.timeRecord) {
                dispatch(getTask(localTask._id, (data) => {
                    if (data) setLocalTask(data);
                }));
                dispatch(triggerRefresh({ triggerName: 'project' }));
                dispatch(showAlert({ severity: "success", message: "Time record added successfully" }));
            } else {
                dispatch(showAlert({ severity: "error", message: response?.message || "Error adding time record" }));
            }
        }));
    };

    return (
        <StyledDialog open={!!localTask} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen} key={localTask._id}>
            <DialogHeader>
                {isEditing ? (
                    <TextField value={localTask.name} onChange={handleChange("name")} fullWidth size="small" label="Task name" disabled={isLocked} />
                ) : (
                    <TaskTitle>{localTask.name}</TaskTitle>
                )}
                {!isEditing && (
                    <HeaderActions>
                        {canEditProjectTask && localTask.hasBoard && canSeeInternal && (
                            <Tooltip title={externalBoard ? `Open QA board on ${externalBoard.host}` : "Open QA board"}>
                                <IconButton
                                    aria-label="Open QA board"
                                    size="small"
                                    {...(externalBoard
                                        ? { component: 'a', href: externalBoard.href, target: '_blank', rel: 'noopener noreferrer' }
                                        : { onClick: handleOpenBoard })}
                                >
                                    <ViewKanban fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {canEditProjectTask && !editsBlocked && (
                            <Tooltip title="Edit task">
                                <IconButton onClick={handleEdit} aria-label="Edit task" size="small">
                                    <Edit fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {canEditProjectTask && canDeleteProjectTask && !localTask.isDeleted && !isLocked && (
                            <Tooltip title="Delete task">
                                <IconButton onClick={onDeleteTask} aria-label="Delete task" size="small">
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title="Close">
                            <IconButton onClick={onClose} aria-label="Close" size="small">
                                <Close fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </HeaderActions>
                )}
            </DialogHeader>
            <DialogContent>
                <Section>
                    <ChipRow>
                        <StatusChip id={localTask._id} status={localTask.status} remainingDays={localTask.remainingDays} parentType="task" />
                        {isEditing ? (
                            <PriorityChip priority={localTask.priority} onChange={isLocked ? undefined : handleChange("priority")} />
                        ) : (
                            <PriorityChip priority={localTask.priority} />
                        )}
                        {!isEditing && (
                            <Chip
                                size="small"
                                color={localTask.readiness === "Blocked" ? "error" : localTask.readiness === "Has Issues" ? "warning" : "success"}
                                label={localTask.readiness}
                            />
                        )}
                        {!isEditing && <SkillChip skill={skills.find((s) => s._id === localTask.skill?._id)?.name} />}
                        {!isEditing && <DepartmentChip department={localTask.department?.name} />}
                    </ChipRow>
                </Section>

                <Section>
                    <SectionHeader>
                        <DescriptionOutlined fontSize="small" />
                        <SectionLabel>Overview</SectionLabel>
                    </SectionHeader>
                    {isEditing ? (
                        <>
                            <MarkdownEditor initialContent={localTask.overview} onChange={(content) => { if (isLocked) return; setLocalTask({ ...localTask, overview: content }); }} />
                            <Box mt={2}>
                                <InputLabel>Skill</InputLabel>
                                <Select fullWidth value={selectedSkillId} onChange={handleChange("skill")} label="Skill" disabled={isLocked}>
                                    {selectableSkills(skills, selectedSkillId).map((skill) => (
                                        <MenuItem key={skill._id} value={skill._id}>
                                            {skillOptionLabel(skill)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                            <Box mt={2}>
                                <InputLabel>Department</InputLabel>
                                <Select fullWidth value={selectedDepartmentId} onChange={handleChange("department")} label="Department" disabled={isLocked}>
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {departments.map((department) => (
                                        <MenuItem key={department._id} value={department._id}>
                                            {department.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        </>
                    ) : localTask.overview ? (
                        <MarkdownDisplay content={localTask.overview} />
                    ) : (
                        <EmptyText>No overview provided.</EmptyText>
                    )}
                </Section>

                {(isEditing || taskTags.length > 0) && (
                    <Section>
                        {tagsEditable ? (
                            <TagPicker draft={tagDraft} targetType="Task" targetId={localTask._id} clientId={tagClientId} clientName={tagClientName} accountId={taskProject?.account || localTask?.account} sourceNames={tagSources} disabled={isLocked} />
                        ) : (
                            <TagPicker value={taskTags} targetType="Task" targetId={localTask._id} sourceNames={tagSources} readOnly lockedReason={isEditing && tagsClosed ? 'Completed work can’t be retagged' : undefined} />
                        )}
                    </Section>
                )}

                {canSeeInternal && hasHumanContext(taskContext) && (
                    <Section>
                        <SectionHeader>
                            <LanguageOutlined fontSize="small" />
                            <SectionLabel>Context</SectionLabel>
                        </SectionHeader>
                        <TagContextDetails context={taskContext} compact />
                    </Section>
                )}

                <Section>
                    <SectionHeader>
                        <AttachFileOutlined fontSize="small" />
                        <SectionLabel>Attachments</SectionLabel>
                    </SectionHeader>
                    {isEditing ? (
                        <FilePondUploader setAttachments={setEditedAttachments} files={localTaskAttachments} onUploadStatusChange={setIsUploading} disabled={isLocked} />
                    ) : localTask.attachments && localTask.attachments.length > 0 ? (
                        <AttachmentList>
                            {localTask.attachments.map((attachment) => (
                                <AttachmentBox
                                    key={attachment._id}
                                    attachment={attachment}
                                    onReupload={(newAttachment) => setLocalTask(prev => ({ ...prev, attachments: [...prev.attachments, newAttachment] }))}
                                />
                            ))}
                        </AttachmentList>
                    ) : (
                        <EmptyText>No attachments.</EmptyText>
                    )}
                </Section>

                {!localTask.isDeleted && !isEditing && (
                    <Section>
                        <TaskChecklistSection
                            task={localTask}
                            isLocked={isLocked}
                            onTaskUpdated={(updatedTask) =>
                                setLocalTask((prev) => ({ ...prev, checklist: updatedTask.checklist, status: updatedTask.status, completedDate: updatedTask.completedDate }))
                            }
                        />
                    </Section>
                )}

                {!localTask.isDeleted && (
                    <Section>
                        <TaskBlockersSection
                            task={localTask}
                            projectTasks={projectTasks}
                            canEdit={(canEditProjectTask || isResponsibleForTask) && !isLocked && !isEditing}
                            onUpdated={handleBlockersUpdated}
                        />
                    </Section>
                )}

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Section>
                            <SectionHeader>
                                <AccessTime fontSize="small" />
                                <SectionLabel>Time &amp; Billing</SectionLabel>
                            </SectionHeader>
                            {isEditing ? (
                                <>
                                    <TextField
                                        label="Budgeted hours"
                                        type="number"
                                        value={localTask.budget?.budgeted}
                                        onChange={(e) => setLocalTask({ ...localTask, budget: { ...localTask.budget, budgeted: e.target.value } })}
                                        fullWidth
                                        margin="normal"
                                        disabled={isLocked}
                                    />
                                    <FormControl fullWidth margin="normal" disabled={isLocked}>
                                        <InputLabel id="billing-type-label">Billing type</InputLabel>
                                        <Select labelId="billing-type-label" label="Billing type" value={localTask.billingType} onChange={handleChange("billingType")}>
                                            <MenuItem value="Hourly">Hourly</MenuItem>
                                            <MenuItem value="Retainer">Retainer</MenuItem>
                                            <MenuItem value="Fixed Price">Fixed Price</MenuItem>
                                            <MenuItem value="Internal">Internal</MenuItem>
                                            <MenuItem value="Non Billable">Non Billable</MenuItem>
                                            <MenuItem value="Parent">Parent</MenuItem>
                                        </Select>
                                    </FormControl>
                                </>
                            ) : (
                                <InfoPanel>
                                    <BudgetBar variant="determinate" color={overBudget ? "error" : "primary"} value={timeSpentPercentage} />
                                    <BudgetText over={overBudget}>
                                        {usedHours} / {budgetedHours} hours
                                    </BudgetText>
                                    <CaptionText>Billing: {localTask.billingType}</CaptionText>
                                    {canAddTime && (
                                        <AddTimeButton
                                            size="small"
                                            startIcon={<Add />}
                                            onClick={() => setAddTimeDialogOpen(true)}
                                            disabled={isLocked}
                                        >
                                            Add Time
                                        </AddTimeButton>
                                    )}
                                </InfoPanel>
                            )}
                        </Section>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Section>
                            <SectionHeader>
                                <CalendarToday fontSize="small" />
                                <SectionLabel>Dates</SectionLabel>
                            </SectionHeader>
                            {isEditing ? (
                                <Box display="flex" flexDirection="column" gap={2} pt={2}>
                                    <AppDatePicker
                                        label="Start Date"
                                        value={formatDate(localTask.startDate)}
                                        onChange={(v) => handleChange("startDate")({ target: { value: v } })}
                                        disabled={isLocked}
                                    />
                                    <AppDatePicker
                                        label="Due Date"
                                        value={formatDate(localTask.dueDate)}
                                        onChange={(v) => handleChange("dueDate")({ target: { value: v } })}
                                        disabled={isLocked}
                                        maxDate={formatDate(taskProject?.dueDate) || undefined}
                                    />
                                </Box>
                            ) : (
                                <InfoPanel>
                                    <MetaRow>
                                        <MetaLabel>Start</MetaLabel>
                                        <MetaValue>{formatDate(localTask.startDate)}</MetaValue>
                                    </MetaRow>
                                    <MetaRow>
                                        <MetaLabel>Due</MetaLabel>
                                        <MetaValue>{formatDate(localTask.dueDate)}</MetaValue>
                                    </MetaRow>
                                    <MetaRow>
                                        <MetaLabel>Timing</MetaLabel>
                                        <Chip size="small" color={remainingDays < 0 ? "error" : "default"} label={formatDueLabel(localTask.dueDate)} />
                                    </MetaRow>
                                </InfoPanel>
                            )}
                        </Section>
                    </Grid>
                </Grid>

                <Section>
                    <SectionHeader>
                        <Group fontSize="small" />
                        <SectionLabel>Team</SectionLabel>
                    </SectionHeader>
                    <TeamBlock>
                        <Box>
                            <FieldLabel>Responsible</FieldLabel>
                            <UserMenuChip
                                user={localTask.responsible}
                                size="small"
                                canSeeUnassigned={canAssignTask && !isLocked}
                                onReAssign={() => setAssignDialogOpen(true)}
                                menuItems={{ reAssign: canAssignTask && !isLocked }}
                            />
                        </Box>
                        <Box>
                            <FieldLabel>Assignees</FieldLabel>
                            <ChipRow>
                                {localTask.assignees.length > 0 ? (
                                    localTask.assignees.map((assignee, index) => (
                                        <UserMenuChip key={index} user={assignee} size="small" canSeeUnassigned={canAssignTask} />
                                    ))
                                ) : (
                                    <EmptyText>No assignees.</EmptyText>
                                )}
                                {canAssignTask && !isLocked && (
                                    <Tooltip title="Add or remove assignees">
                                        <Chip
                                            size="small"
                                            color="default"
                                            variant="outlined"
                                            label="Assign"
                                            avatar={<Add />}
                                            onClick={() => setAssignDialogOpen(true)}
                                        />
                                    </Tooltip>
                                )}
                            </ChipRow>
                        </Box>
                    </TeamBlock>
                </Section>

                <Section>
                    <SectionHeader>
                        <InfoOutlined fontSize="small" />
                        <SectionLabel>Additional Information</SectionLabel>
                    </SectionHeader>
                    <InfoPanel>
                        <MetaRow>
                            <MetaLabel>Rush job</MetaLabel>
                            {isEditing ? (
                                <Switch checked={localTask.rush} onChange={handleSwitchChange("rush")} color="primary" disabled={isLocked} />
                            ) : (
                                <Chip
                                    size="small"
                                    icon={localTask.rush ? <CheckCircle fontSize="small" /> : undefined}
                                    color={localTask.rush ? "error" : "default"}
                                    label={localTask.rush ? "Yes" : "No"}
                                />
                            )}
                        </MetaRow>
                        <MetaRow>
                            <MetaLabel>Deadline status</MetaLabel>
                            <Chip
                                size="small"
                                icon={deadlineStatus === "On Track" ? <CheckCircle fontSize="small" /> : <Warning fontSize="small" />}
                                color={deadlineStatus === "On Track" ? "success" : deadlineStatus === "Upcoming" ? "warning" : "error"}
                                label={deadlineStatus}
                            />
                        </MetaRow>
                        {canEditProjectTask && !localTask.isDeleted && (
                            <MetaRow>
                                <MetaLabel>QA board</MetaLabel>
                                <Tooltip title={localTask.hasBoard ? "QA board enabled" : "QA board disabled"}>
                                    <Switch
                                        checked={!!localTask.hasBoard}
                                        onChange={handleToggleBoard}
                                        color="primary"
                                        disabled={isLocked || boardToggleBusy}
                                        inputProps={{ "aria-label": "Toggle QA board" }}
                                    />
                                </Tooltip>
                            </MetaRow>
                        )}
                    </InfoPanel>
                </Section>
            </DialogContent>
            {isEditing && (
                <DialogActions>
                    <Button
                        onClick={() => {
                            setIsEditing(false);
                            setLocalTask(task);
                            setSelectedSkillId(task.skill?._id || "");
                            if (editMode) {
                                onClose(task);
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleSave} variant="contained" color="primary" disabled={isUploading || editsBlocked || tagDraft.loading}>
                        {isUploading ? <CircularProgress size={20} /> : "Save"}
                    </Button>
                </DialogActions>
            )}
            <UserAssignmentDialog
                isOpen={assignDialogOpen}
                onClose={() => setAssignDialogOpen(false)}
                objectId={localTask._id}
                objectType="task"
                isNewObject={false}
                onAssign={handleTeamAssigned}
            />

            <AddTimeRecordDialog
                open={addTimeDialogOpen}
                onClose={() => setAddTimeDialogOpen(false)}
                onAddTime={handleAddTime}
                // From the task rather than the current project: this dialog is also opened from
                // the master report, which never loads one.
                accountId={localTask.account?._id || localTask.account || localTask.project?.account?._id || project?.account?._id || project?.account}
            />
        </StyledDialog>
    );
};

export default TaskDetailsDialog;
